import {
    BillActivationStatus,
    BillCategory,
    type BillingCadence,
    BillingCadenceOrder,
    type DeepPartial,
    type MoneyMapAccountWithAccountDetails,
    type MoneyMapWithRulesAndAccounts,
} from '@fruitful/types';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    ArrowDownIcon,
    ArrowUpDownIcon,
    ArrowUpIcon,
    BodyMediumRegular,
    BodySmallRegular,
    CirclePlusIcon,
    H6Emphasized,
    Icon,
    LabelRegular,
    Shadows,
    XStack,
    YStack,
} from 'smoothie';

import { useUpsertBillAndRefetch } from '../../bills/useUpsertBill';
import { Button } from '../../common/components/GroveButtons';
import { TextError } from '../../common/components/InlineError';
import type { Bill } from '../../entities/entityTypes';
import { useMembershipBills } from '../../entities/membership/hooks/useMembershipBills';
import {
    BILL_CATEGORY_MEMBER_UI_ORDER,
    billCategoryDisplayName,
    parseBillCategory,
} from '../utils/billCategoryDisplay';
import { moneyMapperBillCadencePickerOptions } from '../utils/moneyMapperBillCadencePickerOptions';
import { MoneyMapperBillDetailsModal } from './MoneyMapperBillDetailsModal';
import { MoneyMapperBillsTableRow } from './MoneyMapperBillsTableRow';
import { MoneyMapperNewBillRow } from './MoneyMapperNewBillRow';
import { MoneyMapperPanelScrollView } from './MoneyMapperPanelScrollView';
import { MoneyMapperSectionHeading } from './MoneyMapperSectionHeading';
import { MonthlyBillsFooter } from './MonthlyBillsFooter';
import {
    INCOME_TABLE_ACTIONS_OUTER_WIDTH,
    INCOME_TABLE_CARD_EDGE_PADDING,
    INCOME_TABLE_ROW_PADDING_HORIZONTAL,
    incomeTableAmountColumnProps,
    incomeTableBillCategoryColumnProps,
    incomeTablePayCycleColumnProps,
    incomeTableSourceColumnProps,
} from './take-home-pay/moneyMapperIncomeTableLayout';
import { useMoneyMapperGuideMonthlyBills } from './useMoneyMapperGuideMonthlyBills';

/** Filter value for the pill row: every bill, or one `BillCategory`. */
const MONEY_MAPPER_BILLS_FILTER_ALL = 'ALL';

type MoneyMapperBillsCategoryFilter =
    | typeof MONEY_MAPPER_BILLS_FILTER_ALL
    | BillCategory;

const categoryTabLabel = (category: MoneyMapperBillsCategoryFilter): string => {
    if (category === MONEY_MAPPER_BILLS_FILTER_ALL) {
        return 'All';
    }
    return billCategoryDisplayName(category);
};

const categoryOrderIndex = (category: BillCategory): number => {
    const i = BILL_CATEGORY_MEMBER_UI_ORDER.indexOf(category);
    return i === -1 ? BILL_CATEGORY_MEMBER_UI_ORDER.length : i;
};

const isBillVisible = (bill: Bill): boolean =>
    bill.activationStatus !== BillActivationStatus.REMOVED &&
    bill.activationStatus !== BillActivationStatus.REJECTED;

const nonEmptyTrimmed = (
    value: string | null | undefined,
): string | undefined => {
    const t = value?.trim();
    return t != null && t !== '' ? t : undefined;
};

const billDisplayName = (bill: Bill): string =>
    nonEmptyTrimmed(bill.displayName) ??
    nonEmptyTrimmed(bill.payee) ??
    nonEmptyTrimmed(bill.name) ??
    '-';

type BillsSortColumn = 'name' | 'category' | 'amount' | 'frequency';

interface BillsSortState {
    column: BillsSortColumn;
    direction: 'asc' | 'desc';
}

const cadenceSortOrder = (cadence: BillingCadence): number =>
    BillingCadenceOrder[cadence] ?? 999;

const SortableTableHeaderCell = ({
    column,
    label,
    sortState,
    onToggleSort,
    ...columnProps
}: {
    column: BillsSortColumn;
    label: string;
    sortState: BillsSortState | null;
    onToggleSort: (column: BillsSortColumn) => void;
} & React.ComponentProps<typeof YStack>) => {
    const active = sortState?.column === column;
    const direction = sortState?.direction;
    const SortIcon =
        active && direction != null
            ? direction === 'asc'
                ? ArrowUpIcon
                : ArrowDownIcon
            : ArrowUpDownIcon;

    return (
        <YStack
            {...columnProps}
            onPress={() => {
                onToggleSort(column);
            }}
            hoverStyle={{ cursor: 'pointer' }}
        >
            <XStack gap={6} alignItems="center">
                <LabelRegular color={active ? '$gray1200' : '$gray1000'}>
                    {label}
                </LabelRegular>
                <Icon
                    icon={SortIcon}
                    size={14}
                    color={active ? '$gray1200' : '$gray1000'}
                />
            </XStack>
        </YStack>
    );
};

export const MoneyMapperBillsTabPanel = ({
    accounts,
    isSaving,
    membershipId,
    moneyMap,
    onUpdateMoneyMap,
}: {
    accounts: MoneyMapAccountWithAccountDetails[];
    isSaving: boolean;
    membershipId: string | null;
    moneyMap: DeepPartial<MoneyMapWithRulesAndAccounts>;
    onUpdateMoneyMap: (
        updates: DeepPartial<MoneyMapWithRulesAndAccounts>,
    ) => Promise<boolean>;
}) => {
    const { membershipBills, isLoadingBills, isBillsError } =
        useMembershipBills(membershipId);

    const { upsertBillAsync } = useUpsertBillAndRefetch(membershipId ?? '');

    const bills = membershipBills.bills ?? [];

    const visibleBills = useMemo(() => bills.filter(isBillVisible), [bills]);

    const [selectedCategory, setSelectedCategory] =
        useState<MoneyMapperBillsCategoryFilter>(MONEY_MAPPER_BILLS_FILTER_ALL);
    const [detailsBillId, setDetailsBillId] = useState<string | null>(null);
    const [showNewBillRow, setShowNewBillRow] = useState(false);
    const [sortState, setSortState] = useState<BillsSortState | null>(null);

    useEffect(() => {
        setSelectedCategory(MONEY_MAPPER_BILLS_FILTER_ALL);
    }, [membershipId]);

    useEffect(() => {
        setShowNewBillRow(false);
    }, [membershipId, selectedCategory]);

    useEffect(() => {
        setSortState(null);
    }, [selectedCategory]);

    const billsInCategory = useMemo(() => {
        if (selectedCategory === MONEY_MAPPER_BILLS_FILTER_ALL) {
            return [...visibleBills].sort((a, b) => {
                const ca = parseBillCategory(a);
                const cb = parseBillCategory(b);
                const ia = categoryOrderIndex(ca);
                const ib = categoryOrderIndex(cb);
                if (ia !== ib) {
                    return ia - ib;
                }
                return b.monthlyAmount - a.monthlyAmount;
            });
        }
        return visibleBills.filter(
            (bill) => parseBillCategory(bill) === selectedCategory,
        );
    }, [visibleBills, selectedCategory]);

    const sortedBills = useMemo(() => {
        if (sortState == null) {
            return billsInCategory;
        }
        const directionMultiplier = sortState.direction === 'asc' ? 1 : -1;
        return [...billsInCategory].sort((a, b) => {
            let comparison = 0;
            if (sortState.column === 'name') {
                comparison = billDisplayName(a).localeCompare(
                    billDisplayName(b),
                    undefined,
                    { sensitivity: 'base' },
                );
            } else if (sortState.column === 'category') {
                const ia = categoryOrderIndex(parseBillCategory(a));
                const ib = categoryOrderIndex(parseBillCategory(b));
                comparison = ia - ib;
                if (comparison === 0) {
                    comparison = billCategoryDisplayName(
                        parseBillCategory(a),
                    ).localeCompare(
                        billCategoryDisplayName(parseBillCategory(b)),
                        undefined,
                        { sensitivity: 'base' },
                    );
                }
            } else if (sortState.column === 'amount') {
                comparison = a.monthlyAmount - b.monthlyAmount;
            } else {
                comparison =
                    cadenceSortOrder(a.cadence) - cadenceSortOrder(b.cadence);
            }
            return comparison * directionMultiplier;
        });
    }, [billsInCategory, sortState]);

    const handleSortHeaderPress = useCallback((column: BillsSortColumn) => {
        setSortState((prev) => {
            if (prev?.column === column) {
                return {
                    column,
                    direction: prev.direction === 'asc' ? 'desc' : 'asc',
                };
            }
            return { column, direction: 'asc' };
        });
    }, []);

    const allMonthlyTotalCents = useMemo(() => {
        return visibleBills.reduce((sum, b) => sum + b.monthlyAmount, 0);
    }, [visibleBills]);

    const guideTotal = useMoneyMapperGuideMonthlyBills({
        accounts,
        membershipBillsSumCents: allMonthlyTotalCents,
        moneyMap,
        onUpdateMoneyMap,
        isSaving,
    });

    const detailsBill = useMemo(() => {
        if (detailsBillId == null) {
            return null;
        }
        return bills.find((b) => b.id === detailsBillId) ?? null;
    }, [detailsBillId, bills]);

    const newBillCategoryPickerOptions = useMemo(
        () =>
            BILL_CATEGORY_MEMBER_UI_ORDER.map((c) => ({
                value: c,
                label: billCategoryDisplayName(c),
            })),
        [],
    );

    if (membershipId == null) {
        return (
            <BodyMediumRegular color="$gray1000">
                Select a membership to view bills.
            </BodyMediumRegular>
        );
    }

    if (isBillsError) {
        return (
            <TextError message="Could not load bills for this membership." />
        );
    }

    if (isLoadingBills) {
        return (
            <BodyMediumRegular color="$gray1000" fontStyle="italic">
                Loading bills…
            </BodyMediumRegular>
        );
    }

    return (
        <YStack flex={1} minHeight={0} justifyContent="flex-start">
            <MoneyMapperPanelScrollView style={{ flex: 1, minHeight: 0 }}>
                <YStack gap={20} paddingBottom={4}>
                    <MoneyMapperSectionHeading
                        title="Bills"
                        subtitle="Monthly recurring bills and debt"
                    />

                    <MoneyMapperPanelScrollView
                        horizontal
                        showsHorizontalScrollIndicator={false}
                        contentContainerStyle={{ flexGrow: 0 }}
                    >
                        <XStack
                            gap={8}
                            paddingBottom={4}
                            paddingHorizontal={INCOME_TABLE_CARD_EDGE_PADDING}
                            flexWrap="nowrap"
                        >
                            <XStack
                                key={MONEY_MAPPER_BILLS_FILTER_ALL}
                                paddingHorizontal={14}
                                paddingVertical={8}
                                borderRadius={999}
                                backgroundColor={
                                    selectedCategory ===
                                    MONEY_MAPPER_BILLS_FILTER_ALL
                                        ? '$gray1200'
                                        : '$gray400'
                                }
                                hoverStyle={{ cursor: 'pointer' }}
                                onPress={() => {
                                    setSelectedCategory(
                                        MONEY_MAPPER_BILLS_FILTER_ALL,
                                    );
                                }}
                            >
                                <BodySmallRegular
                                    color={
                                        selectedCategory ===
                                        MONEY_MAPPER_BILLS_FILTER_ALL
                                            ? '$white'
                                            : '$gray1100'
                                    }
                                >
                                    {categoryTabLabel(
                                        MONEY_MAPPER_BILLS_FILTER_ALL,
                                    )}
                                </BodySmallRegular>
                            </XStack>
                            {BILL_CATEGORY_MEMBER_UI_ORDER.map((category) => {
                                const selected = selectedCategory === category;
                                return (
                                    <XStack
                                        key={category}
                                        paddingHorizontal={14}
                                        paddingVertical={8}
                                        borderRadius={999}
                                        backgroundColor={
                                            selected ? '$gray1200' : '$gray400'
                                        }
                                        hoverStyle={{ cursor: 'pointer' }}
                                        onPress={() => {
                                            setSelectedCategory(category);
                                        }}
                                    >
                                        <BodySmallRegular
                                            color={
                                                selected
                                                    ? '$white'
                                                    : '$gray1100'
                                            }
                                        >
                                            {categoryTabLabel(category)}
                                        </BodySmallRegular>
                                    </XStack>
                                );
                            })}
                        </XStack>
                    </MoneyMapperPanelScrollView>

                    <YStack
                        backgroundColor="$white"
                        width="100%"
                        borderRadius={24}
                        paddingTop={24}
                        paddingBottom={24}
                        gap={0}
                        {...Shadows.SMALL}
                    >
                        <YStack
                            paddingHorizontal={INCOME_TABLE_CARD_EDGE_PADDING}
                        >
                            <H6Emphasized color="$gray1200">
                                {categoryTabLabel(selectedCategory)}
                            </H6Emphasized>
                            <BodySmallRegular
                                color="$gray1000"
                                marginBottom={12}
                            >
                                {selectedCategory ===
                                MONEY_MAPPER_BILLS_FILTER_ALL
                                    ? 'All bills'
                                    : 'Accounted in the monthly total.'}
                            </BodySmallRegular>
                        </YStack>

                        <XStack
                            width="100%"
                            alignItems="flex-end"
                            paddingVertical={12}
                            paddingHorizontal={
                                INCOME_TABLE_ROW_PADDING_HORIZONTAL
                            }
                            gap={8}
                        >
                            <SortableTableHeaderCell
                                column="name"
                                label="Bill name"
                                sortState={sortState}
                                onToggleSort={handleSortHeaderPress}
                                {...incomeTableSourceColumnProps}
                            />
                            {selectedCategory ===
                            MONEY_MAPPER_BILLS_FILTER_ALL ? (
                                <SortableTableHeaderCell
                                    column="category"
                                    label="Category"
                                    sortState={sortState}
                                    onToggleSort={handleSortHeaderPress}
                                    {...incomeTableBillCategoryColumnProps}
                                />
                            ) : null}
                            <SortableTableHeaderCell
                                column="amount"
                                label="Amount"
                                sortState={sortState}
                                onToggleSort={handleSortHeaderPress}
                                {...incomeTableAmountColumnProps}
                            />
                            <SortableTableHeaderCell
                                column="frequency"
                                label="Frequency"
                                sortState={sortState}
                                onToggleSort={handleSortHeaderPress}
                                {...incomeTablePayCycleColumnProps}
                            />
                            <XStack
                                width={INCOME_TABLE_ACTIONS_OUTER_WIDTH}
                                flexShrink={0}
                            />
                        </XStack>

                        {sortedBills.length === 0 && !showNewBillRow ? (
                            <YStack
                                paddingVertical={28}
                                paddingHorizontal={
                                    INCOME_TABLE_ROW_PADDING_HORIZONTAL
                                }
                                alignItems="center"
                            ></YStack>
                        ) : (
                            <>
                                {sortedBills.map((bill) => (
                                    <MoneyMapperBillsTableRow
                                        key={bill.id}
                                        bill={bill}
                                        canMutate={!isSaving}
                                        showCategoryPicker={
                                            selectedCategory ===
                                            MONEY_MAPPER_BILLS_FILTER_ALL
                                        }
                                        categoryPickerOptions={
                                            newBillCategoryPickerOptions
                                        }
                                        cadencePickerOptions={
                                            moneyMapperBillCadencePickerOptions
                                        }
                                        upsertBillAsync={upsertBillAsync}
                                        onOpenDetails={() => {
                                            setDetailsBillId(bill.id);
                                        }}
                                    />
                                ))}
                                {showNewBillRow ? (
                                    <MoneyMapperNewBillRow
                                        key={
                                            selectedCategory ===
                                            MONEY_MAPPER_BILLS_FILTER_ALL
                                                ? 'new-bill-all'
                                                : selectedCategory
                                        }
                                        membershipId={membershipId}
                                        category={
                                            selectedCategory ===
                                            MONEY_MAPPER_BILLS_FILTER_ALL
                                                ? BillCategory.OTHER
                                                : selectedCategory
                                        }
                                        categoryPickerOptions={
                                            selectedCategory ===
                                            MONEY_MAPPER_BILLS_FILTER_ALL
                                                ? newBillCategoryPickerOptions
                                                : undefined
                                        }
                                        disabled={isSaving}
                                        onSaved={() => {
                                            setShowNewBillRow(false);
                                        }}
                                    />
                                ) : null}
                            </>
                        )}

                        <XStack
                            width="100%"
                            justifyContent="flex-start"
                            alignItems="center"
                            gap={16}
                            paddingHorizontal={INCOME_TABLE_CARD_EDGE_PADDING}
                            paddingTop={
                                sortedBills.length === 0 && !showNewBillRow
                                    ? 20
                                    : 16
                            }
                            paddingBottom={4}
                            flexWrap="wrap"
                        >
                            {showNewBillRow ? (
                                <Button
                                    variant="secondary"
                                    size="large"
                                    disabled={isSaving}
                                    onPress={() => {
                                        setShowNewBillRow(false);
                                    }}
                                >
                                    Cancel
                                </Button>
                            ) : (
                                <Button
                                    variant="secondary"
                                    size="large"
                                    icon={CirclePlusIcon}
                                    disabled={showNewBillRow || isSaving}
                                    onPress={() => {
                                        setShowNewBillRow(true);
                                    }}
                                >
                                    Add a bill
                                </Button>
                            )}
                        </XStack>
                    </YStack>
                </YStack>
            </MoneyMapperPanelScrollView>

            <YStack flexShrink={0} paddingTop={12} paddingBottom={16}>
                <MonthlyBillsFooter
                    guideTotal={guideTotal}
                    isSaving={isSaving}
                />
            </YStack>

            <MoneyMapperBillDetailsModal
                bill={detailsBill}
                isOpen={detailsBill != null}
                membershipId={membershipId}
                onClose={() => {
                    setDetailsBillId(null);
                }}
            />
        </YStack>
    );
};
